import { Box } from '@material-ui/core';
import { FormatListNumberedOutlined, SearchOutlined, TouchApp } from '@material-ui/icons';
import { chain } from 'lodash';
import React from 'react';
import { AnalysisChartLoading } from '../../../AnalysisChartLoading';
import { DomainTypeGq } from '../../../../../graphql/projects/models/DomainTypeGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { DomainPerformanceAnalysisGq } from '../../../../../graphql/projects/queries/analysis/useGetCompetitivePerformanceGq';
import { formatNumber } from '../../../../../logic/otherUtilities/formatting/formatNumber';
import { formatPercent } from '../../../../../logic/otherUtilities/formatting/formatPercent';
import { ChartProps } from '../../../AnalysisList';
import { internationalText } from '../../../internationalText';
import Item, { ItemAnalysis } from './Item';
import { formatTemplate } from '../../../../../logic/otherUtilities/formatting/formatTemplate';
import DataTimedOut, { didQueryTimeout } from '../../DataTimedOut';

export interface PerformanceCompetitorsProps extends ChartProps {}

export const PerformanceCompetitors: React.FC<PerformanceCompetitorsProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  React.useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showDomainIds: true,
          showTagGroupId: true,
          showTagId: true,
          showCompetitorColor: true,
          showTargetRankValues: true,
        },
      })),
    []
  );

  // Data fetching
  const dataQuery = projectsApiService.queries.analysis.useGetCompetitivePerformance({
    projectId: project.id,
    domainIds: analysisFilterState?.domainIds,
    tagGroupId: analysisFilterState?.tagGroupId,
    tagId: analysisFilterState?.tagId,
    rankValues: analysisFilterState?.targetRankValues ?? [1],
  });

  const domainAnalyses: DomainPerformanceAnalysisGq[] =
    dataQuery?.result?.domainPerformanceAnalyses ?? [];

  let maxTraffic = dataQuery?.result?.totalTraffic ?? 0;
  const data = chain(project.domains)
    .filter((d) => analysisFilterState?.domainIds.includes(d.id))
    .map((domain): ItemAnalysis => {
      const domainAnalysis = domainAnalyses.find(
        (domainAnalysis) => domainAnalysis.domain.id === domain.id
      );
      if (domainAnalysis?.traffic > 0) maxTraffic = Math.max(maxTraffic, domainAnalysis?.traffic);
      return {
        domain,
        domainAnalysis,
      };
    })
    .orderBy(
      [
        (d) => d.domainAnalysis?.traffic ?? 0,
        (d) => d.domainAnalysis?.coveredKeywordCount ?? 0,
        (d) => d.domainAnalysis?.coveredSearchVolume ?? 0,
        (d) => d.domain.id,
      ],
      ['desc', 'desc', 'desc', 'asc']
    )
    .value();

  if (didQueryTimeout(dataQuery?.query)) {
    return <DataTimedOut />;
  }

  if (dataQuery?.query?.loading || !dataQuery.result) {
    return <AnalysisChartLoading />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <div className="min-h-[500px] w-full flex justify-between gap-16 py-8">
        <div className="w-full">
          <Item
            palette={palette}
            Icon={FormatListNumberedOutlined}
            title={internationalText.chart.performance_vs_competitors.rankings.label[language]}
            description={formatTemplate(
              internationalText.chart.performance_vs_competitors.rankings.desc[language],
              [formatNumber(dataQuery?.result?.totalKeywordCount)]
            )}
            data={data}
            getValue={(dataPoint) =>
              dataQuery.result.totalKeywordCount === 0
                ? 0
                : ((dataPoint.domainAnalysis?.coveredKeywordCount ?? 0) * 100) /
                  dataQuery.result.totalKeywordCount
            }
            getFormattedValue={(dataPoint) =>
              formatPercent(
                dataQuery.result.totalKeywordCount === 0
                  ? 0
                  : (dataPoint.domainAnalysis?.coveredKeywordCount ?? 0) /
                      dataQuery.result.totalKeywordCount
              ) + '%'
            }
            analysisFilterState={analysisFilterState}
          />
        </div>
        <div className="w-full">
          <Item
            palette={palette}
            Icon={SearchOutlined}
            title={internationalText.chart.performance_vs_competitors.visibility.label[language]}
            description={formatTemplate(
              internationalText.chart.performance_vs_competitors.visibility.desc[language],
              [formatNumber(dataQuery?.result?.totalSearchVolume)]
            )}
            data={data}
            getValue={(dataPoint) =>
              dataQuery.result.totalSearchVolume === 0
                ? 0
                : ((dataPoint.domainAnalysis?.coveredSearchVolume ?? 0) * 100) /
                  dataQuery.result.totalSearchVolume
            }
            getFormattedValue={(dataPoint) =>
              formatPercent(
                dataQuery.result.totalSearchVolume === 0
                  ? 0
                  : (dataPoint.domainAnalysis?.coveredSearchVolume ?? 0) /
                      dataQuery.result.totalSearchVolume
              ) + '%'
            }
            analysisFilterState={analysisFilterState}
          />
        </div>
        <div className="w-full">
          <Item
            palette={palette}
            Icon={TouchApp}
            title={internationalText.chart.performance_vs_competitors.traffic.label[language]}
            description={formatTemplate(
              internationalText.chart.performance_vs_competitors.traffic.desc[language],
              [formatNumber(maxTraffic)]
            )}
            data={data}
            getValue={(dataPoint) =>
              dataQuery.result.totalTraffic === 0
                ? 0
                : ((dataPoint.domainAnalysis?.traffic ?? 0) * 100) / dataQuery.result.totalTraffic
            }
            getFormattedValue={(dataPoint) => formatNumber(dataPoint.domainAnalysis?.traffic ?? 0)}
            analysisFilterState={analysisFilterState}
          />
        </div>
      </div>
    </div>
  );
};

export default PerformanceCompetitors;
